import { t, Trans } from '@lingui/macro'
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  Grid,
  Typography
} from '@mui/material'
import { dateFormat } from 'app/appSettings'
import { useGlobalQueries } from 'app/GlobalQueriesProvider'
import { ReduxOrganization, ReduxState, ReduxUser } from 'app/redux/ReduxInterfaces'
import sfOauthConfig from 'app/services/sfAuth/sfAuthConfig'
import { createCaseByFlow } from 'app/services/sfAuth/sfData/sfCase'
import { linkFileByFlow, uploadFile } from 'app/services/sfAuth/sfData/sfFiles'
import * as Excel from 'exceljs'
import { saveAs } from 'file-saver'
import { useFormikContext } from 'formik'
import * as h2p from 'html2plaintext'
import _ from 'lodash'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { myI18n } from 'translation/I18nConnectedProvider'
import { DialogTitleWithIconClose } from '../common-components/DialogTitleWithIconClose'
import { FormTopLabel } from '../common-components/forms/FormTopLabel'
import { TextFieldWithCustomControl } from '../common-components/with-formik/TextFieldWithCustomControl'
import { useFormContext } from '../forms/form-page/FormContext'
import { ERROR_REPORTING_USER_ID } from '../forms/FormErrorPopUp'
import { requiredTrans } from '../forms/formTranslations'
import { ConnectedMap } from '../forms/FormTypes'
import { getObjectsIdsFromConnectedMap } from '../forms/formUtils'
import { CASE_TYPES } from 'app/services/sfAuth/sfData/sfCaseTypes'

interface FormValuesData {
  elementType: 'string'
  id: string
  sectionIndex: number
  sectionName: string
  showBasicElements: boolean
  title: string
  value: any
}

interface SavingFailedWarningDialogProps {
  fileName: string
  open: boolean
  handleClose: () => void
  data?: {
    error: string
    savingResult: any
    connectedMap: ConnectedMap
    formData: Record<string, FormValuesData> & { type: string }
    current: Record<string, FormValuesData>
  }
}

interface SavingFailedValue {
  fieldValue: any
  valueLabel: string
  fieldLabel: string
}

const KNOWN_ERRORS: Record<string, { code: string }> = {
  INSUFFICIENT_ACCESS_OR_READONLY: {
    code: '01'
  }
}

const SavingFailedWarningDialog = ({ handleClose, open, data, fileName }: SavingFailedWarningDialogProps): JSX.Element => {
  const [reportErrorOpen, setReportErrorOpen] = useState(false)
  const [errorDescription, setErrorDescription] = useState('')
  const [submittingCase, setSubmittingCase] = useState<boolean>(false)
  const [thankYouOpen, setThankYouOpen] = useState<boolean>(false)
  const [savingFailedData, setSavingFailedData] = useState<SavingFailedValue[]>([])
  const [exporting, setExporting] = useState(false)
  const user = useSelector<ReduxState, ReduxUser>(state => state.user)
  const organization = useSelector<ReduxState, ReduxOrganization>((state) => state.organization)
  const userLang = user.language
  const { network } = useGlobalQueries()
  const { values } = useFormikContext()
  const history = useHistory()
  const { mainConnectedObject } = useFormContext()

  const errorCodeLabel = data != null && KNOWN_ERRORS[data?.error]?.code

  useEffect(() => {
    if (data != null) {
      const { current, formData } = data
      const toSet: SavingFailedValue[] = []
      Object.keys(current).forEach(key => {
        const valueLabel = current[key][userLang]
        const fieldValue = current[key].value
        let labelKey: string = userLang
        if (formData.type === 'form') {
          labelKey = 'title'
        }
        if (valueLabel != null && formData[key][labelKey] != null) {
          toSet.push({
            fieldValue,
            valueLabel,
            fieldLabel: formData[key][labelKey]
          })
        }
      })
      setSavingFailedData(toSet)
    }
  }, [open])

  const downloadCurrentAnswers = async (): Promise<void> => {
    const workbook = new Excel.Workbook()
    const sheet = workbook.addWorksheet(myI18n._(t`Extracted data`), {
      views: [{ state: 'frozen', ySplit: 1 }]
    })
    sheet.columns = [
      { header: 'Field', width: 45, key: 'field' },
      { header: 'Field value', width: 45, key: 'value' }
    ]
    savingFailedData.forEach(obj => {
      const labelString = obj.fieldLabel
      let valueString = obj.fieldValue
      if (Array.isArray(valueString)) {
        if (valueString.length === 0) {
          valueString = ''
        } else if (
          valueString[0] != null && typeof valueString[0] === 'object'
        ) {
          valueString = JSON.stringify(valueString)
        } else {
          valueString = valueString.join(', ')
        }
      } else if (valueString != null && typeof valueString === 'object') {
        valueString = JSON.stringify(valueString)
      } else if (typeof valueString === 'boolean') {
        valueString = valueString ? myI18n._(t`Yes`) : myI18n._(t`No`)
      } else if (/<[a-z/][\s\S]*>/i.test(valueString)) {
        valueString = h2p(valueString)
      }
      sheet.addRow({
        field: labelString,
        value: valueString
      })
    })
    let rowIndex = 1
    for (rowIndex; rowIndex <= sheet.rowCount; rowIndex++) {
      sheet.getRow(rowIndex).alignment = {
        wrapText: true,
        vertical: 'top'
      }
    }
    setExporting(true)
    return await workbook.xlsx.writeBuffer().then(function (data) {
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      })
      saveAs(blob, fileName + '.xlsx')
      setExporting(false)
    })
  }

  const submitErrorReport = async (): Promise<void> => {
    if (data == null) {
      return
    }
    setSubmittingCase(true)
    const toPass: any = _.cloneDeep(values)
    delete toPass.muInfo
    delete toPass.muUsers

    const json = JSON.stringify({
      reject: data?.savingResult,
      error: data?.error,
      errorCodeLabel,
      values: toPass
    })

    const { connectedAccount, connectedOpportunity, editedObjectId, connectedResource } = getObjectsIdsFromConnectedMap(data.connectedMap, mainConnectedObject)

    const caseName = `Saving failed ${moment.utc().format(dateFormat)}`
    createCaseByFlow({
      title: caseName,
      type: CASE_TYPES.SAVING_FAILED,
      language: user.language,
      location: sfOauthConfig.oauth2.redirectUri + history.location.pathname,
      resource: connectedResource,
      contact: user.userObject.contactId,
      opportunityId: connectedOpportunity,
      description: errorDescription,
      organization: connectedAccount ?? organization.id,
      owner: ERROR_REPORTING_USER_ID
    }).then(result => {
      const caseId = result[0].outputValues.createdId
      uploadFile(
        { name: caseName + '.json', uploadId: editedObjectId },
        json,
        network.id,
        true
      )
        .then(result => {
          const fileId = result.Id
          linkFileByFlow({ file: fileId, target: caseId }).then(result => {
            setSubmittingCase(false)
          })
        })
    }).catch(error => {
      console.error(error)
      setSubmittingCase(false)
    })
  }

  return (
    <>
      <Dialog maxWidth='xs' fullWidth open={thankYouOpen}>
        <DialogTitleWithIconClose
          handleClose={() => {
            setThankYouOpen(false)
            history.goBack()
          }}
          label={<Trans>THANK_YOU</Trans>}
        />
        <DialogContent>
          <DialogContentText>
            <Trans>CASE_SUBMITTED_THANK_YOU_TEXT</Trans>
          </DialogContentText>
          <Button
            variant='outlined'
            fullWidth
            sx={{ mt: '8px' }}
            onClick={e => {
              setThankYouOpen(false)
              history.goBack()
            }}
          >
            <Trans>CLOSE</Trans>
          </Button>
        </DialogContent>
      </Dialog>
      <Dialog maxWidth='xs' fullWidth open={reportErrorOpen && !thankYouOpen}>
        <DialogTitleWithIconClose
          handleClose={() => {
            setReportErrorOpen(false)
          }}
          label={<Trans>REPORT_ERROR</Trans>}
        />
        <DialogContent>
          <Typography className='required-fields-label' sx={{ pt: '0px' }}>
            <Trans>
              REQUIRED_FIELDS_PROMPRT_EDITABLE_FORM
            </Trans>
          </Typography>

          <FormTopLabel required>
            <Trans>
              REPORT_ERROR_DESCRIPTION_LABEL
            </Trans>
          </FormTopLabel>

          <TextFieldWithCustomControl
            value={errorDescription}
            handleChange={v => {
              setErrorDescription(v)
            }}
            multiline
            rows={7}
            fullWidth
            required
            error={errorDescription === ''}
            errorLabel={requiredTrans}
          />
          <Grid sx={{ mt: '32px' }} container direction='column' gap='16px'>
            <Button
              color='primary'
              variant='contained'
              fullWidth
              disabled={submittingCase || errorDescription === ''}
              onClick={() => {
                submitErrorReport().catch(err => {
                  console.error(err)
                })
              }}
            >
              <Trans>SUBMIT</Trans>
            </Button>

            <Button
              color='primary'
              variant='outlined'
              fullWidth
              onClick={() => {
                setReportErrorOpen(false)
              }}
            >
              <Trans>CANCEL</Trans>
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog maxWidth='xs' fullWidth open={open && !reportErrorOpen && !thankYouOpen}>
        <DialogTitleWithIconClose
          handleClose={handleClose}
          label={<Trans>ERROR</Trans>}
        />
        <DialogContent>
          <Alert
            severity='error'
          >
            <AlertTitle>
              <Trans>
                FORM_COULD_NOT_BE_SAVED
              </Trans>
              {errorCodeLabel != null && ` (${myI18n._(t`ERROR_CODE`)} ${errorCodeLabel})`}
            </AlertTitle>
            <Typography>
              <Trans>
                FORM_SAVING_FAILED_DOWNLOAD_ANSWER_PROMPT
              </Trans>
            </Typography>
            <Typography>
              <b>
                <Trans>FORM_SAVING_FAILED_DO_NOT_CONTINUE_EDITING_PROMPT</Trans>
              </b>{' '}
              <Trans>FORM_SAVING_FAILED_DO_NOT_CONTINUE_EDITING_PROMPT_CONT</Trans>
            </Typography>
          </Alert>

          <Grid sx={{ mt: '32px' }} container direction='column' gap='16px'>
            <Button
              color='primary'
              variant='contained'
              fullWidth
              disabled={exporting}
              onClick={() => {
                downloadCurrentAnswers().catch(err => {
                  console.error(err)
                })
              }}
            >
              <Trans>DOWNLOAD_ANSWERS</Trans>
            </Button>

            <Button
              color='primary'
              variant='outlined'
              fullWidth
              onClick={() => {
                setReportErrorOpen(true)
              }}
            >
              <Trans>REPORT_ERROR</Trans>
            </Button>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default SavingFailedWarningDialog
